import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useContent } from '../../../context/ContentContext';
import { resolveMediaUrl, getApiUrl } from '../../../utils/api';
import { getSupabase } from '../../../utils/supabase';
import {
  CartItem,
  OrderOutlet,
  OrderType,
  PaymentMethod,
  CustomerProfile,
  SpecialForYouItem,
} from '../../../types';
import { calculateItemUnitPrice } from '../../../data/addOnsData';
import { formatRupiah } from '../../../utils/formatters';
import {
  uploadPaymentReceipt,
  TOTAL_RECEIPT_UPLOAD_TIMEOUT_MS,
  ReceiptUploadResult,
} from '../../../utils/supabaseOrders';
import { isMenuItemAvailableForOutlet } from '../../../utils/supabaseStock';
import {
  subscribeToOutletAvailabilityRealtime,
  normalizeOutletKey,
} from '../../../utils/supabaseOutletStatus';
import {
  getRewardRedemptions,
  RewardRedemption,
} from '../../../utils/supabaseLoyalty';
import {
  ArrowLeft,
  Store,
  UploadCloud,
  FileCheck,
  AlertCircle,
  Bolt,
  ShieldCheck,
  Trash2,
  Check,
  Copy,
  Plus,
  QrCode,
  Lock,
  ExternalLink,
  Ticket,
  Tag,
  X,
  ChevronRight,
  Gift,
  Truck,
  Navigation,
  MapPin,
  Loader2,
  Info,
} from 'lucide-react';
import {
  isOutletDeliverySupported,
  getOutletCoordinates,
} from '../../../data/outletsData';
import {
  calculateHaversineDistanceKm,
  calculateDeliveryFee,
  FIXED_SERVICE_FEE,
} from '../../../utils/deliveryFee';

interface OrderCheckoutProps {
  outlet: OrderOutlet;
  cart: CartItem[];
  generalNote: string;
  onBackToCart: () => void;
  onSubmitOrder: (orderDetails: {
    customerName: string;
    customerPhone: string;
    orderType: OrderType;
    tableNumber: string;
    paymentMethod: PaymentMethod;
    paymentReceiptUrl?: string;
    paymentReceiptPath?: string;
    isMemberChoice?: boolean;
    pickupTime: string;
    voucherId?: string;
    voucherCode?: string;
    discountAmount?: number;
    deliveryAddress?: string;
    customerLat?: number;
    customerLng?: number;
    deliveryDistance?: number;
    deliveryFee?: number;
  }) => Promise<{ success: boolean; error?: string; order?: any } | void>;
  isSubmitting: boolean;
  customerProfile?: CustomerProfile | null;
  initialOrderType?: OrderType;
  onClearCart?: () => void;
  activePromo?: SpecialForYouItem | null;
}

/**
 * Mengambil himpunan identifier voucher (ID dan kode) yang telah dikonfirmasi 
 * tidak dapat digunakan dari sesi atau riwayat pesanan perangkat.
 */
export const getUnusableVoucherKeys = (): Set<string> => {
  const keys = new Set<string>();
  if (typeof window === "undefined") return keys;

  try {
    const sessionRaw = sessionStorage.getItem("leton_unusable_vouchers");
    if (sessionRaw) {
      const arr = JSON.parse(sessionRaw);
      if (Array.isArray(arr)) {
        arr.forEach((k) => keys.add(String(k).trim().toUpperCase()));
      }
    }
    const localRaw = localStorage.getItem("leton_unusable_vouchers");
    if (localRaw) {
      const arr = JSON.parse(localRaw);
      if (Array.isArray(arr)) {
        arr.forEach((k) => keys.add(String(k).trim().toUpperCase()));
      }
    }
  } catch {}

  try {
    const ordersRaw = localStorage.getItem("leton_customer_orders_history");
    if (ordersRaw) {
      const orders = JSON.parse(ordersRaw);
      if (Array.isArray(orders)) {
        orders.forEach((o: any) => {
          const vId = o.voucherId || o.voucher_id;
          const vCode = o.voucherCode || o.voucher_code;
          if (vId) keys.add(String(vId).trim().toUpperCase());
          if (vCode) keys.add(String(vCode).trim().toUpperCase());
        });
      }
    }
  } catch {}

  return keys;
};

export const markVoucherAsUnusable = (v: { id?: string; voucherCode?: string }) => {
  if (typeof window === "undefined" || !v) return;
  const toAdd: string[] = [];
  if (v.id) toAdd.push(String(v.id).trim().toUpperCase());
  if (v.voucherCode) toAdd.push(String(v.voucherCode).trim().toUpperCase());
  if (toAdd.length === 0) return;

  try {
    const existing = Array.from(getUnusableVoucherKeys());
    const combined = Array.from(new Set([...existing, ...toAdd]));
    sessionStorage.setItem("leton_unusable_vouchers", JSON.stringify(combined));
    localStorage.setItem("leton_unusable_vouchers", JSON.stringify(combined));
  } catch {}
};

/**
 * Filter otoritatif voucher yang layak digunakan pada checkout:
 * - Tidak tercatat dalam voucher yang telah ditolak server / terpakai di riwayat lokal
 * - Status otoritatif harus ACTIVE (bukan USED atau EXPIRED)
 * - Belum pernah digunakan (tidak ada referenceOrderId, usedAt, penanda outlet/admin)
 * - Belum kedaluwarsa
 * - Memenuhi kelayakan tipe reward checkout online (DISCOUNT_NOMINAL atau DISCOUNT_PERCENT)
 */
/**
 * Filter otoritatif voucher yang layak digunakan pada checkout:
 * - Status harus ACTIVE (bukan USED atau EXPIRED)
 * - Belum pernah digunakan (tidak ada referenceOrderId, usedAt, penanda outlet/admin)
 * - Belum kedaluwarsa
 * - Memenuhi kelayakan tipe reward checkout online (DISCOUNT_NOMINAL atau DISCOUNT_PERCENT)
 */
export const isVoucherEligibleForCheckout = (v: RewardRedemption): boolean => {
  if (!v) return false;
  if (v.status !== "ACTIVE") return false;
  const isUsed =
    Boolean(v.referenceOrderId) ||
    Boolean((v as any).reference_order_id) ||
    Boolean(v.usedAt) ||
    Boolean((v as any).used_at) ||
    Boolean(v.usedByAdmin) ||
    Boolean((v as any).used_by_admin) ||
    Boolean(v.usedAtOutletId) ||
    Boolean((v as any).used_at_outlet_id);
  if (isUsed) return false;
  if (v.expiresAt) {
    const expTime = new Date(v.expiresAt).getTime();
    if (!isNaN(expTime) && expTime < Date.now()) return false;
  }
  if (v.rewardType !== "DISCOUNT_NOMINAL" && v.rewardType !== "DISCOUNT_PERCENT") {
    return false;
  }
  return true;
};

export const OrderCheckout: React.FC<OrderCheckoutProps> = ({
  outlet,
  cart,
  generalNote,
  onBackToCart,
  onSubmitOrder,
  isSubmitting,
  customerProfile,
  initialOrderType = 'TAKE AWAY',
  onClearCart,
  activePromo = null,
}) => {
  const { data } = useContent();
  const activeQrisUrl = outlet.qrisImage || data?.siteSettings?.qrisImage;

  // DANA Payment Configuration from Central Admin (SiteSettings)
  const isDanaEnabled = data?.siteSettings?.danaEnabled ?? true;
  const danaMinAmount = data?.siteSettings?.danaMinAmount ?? 10000;
  const danaMaxAmount = data?.siteSettings?.danaMaxAmount ?? 2000000;

  // Form State
  const [customerName, setCustomerName] = useState<string>(customerProfile?.namaLengkap || '');
  const [customerPhone, setCustomerPhone] = useState<string>(customerProfile?.nomorHp || '');
  const [isMemberChoice, setIsMemberChoice] = useState<boolean>(customerProfile ? true : false);
  const [orderNote, setOrderNote] = useState<string>(generalNote || '');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('QRIS');
  const [isInitiatingDana, setIsInitiatingDana] = useState<boolean>(false);
  const [pickupTime, setPickupTime] = useState<string>('');
  const [formError, setFormError] = useState<string>('');
  const [savedOrderRef, setSavedOrderRef] = useState<string | null>(null);

  // Auto-fallback: Reset ke QRIS jika DANA dinonaktifkan di pengaturan pusat
  useEffect(() => {
    if (!isDanaEnabled && paymentMethod === 'DANA') {
      setPaymentMethod('QRIS');
    }
  }, [isDanaEnabled, paymentMethod]);

  // Delivery Feature State (Per-outlet capability + Global Super Admin control)
  const isOutletDeliveryCapable = outlet.id === 'sudirman' && (outlet.supportsDelivery ?? isOutletDeliverySupported(outlet.id));
  const [globalDeliveryEnabled, setGlobalDeliveryEnabled] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      try {
        const cached = localStorage.getItem('leton_global_delivery_enabled');
        if (cached !== null) {
          const parsed = JSON.parse(cached);
          if (typeof parsed === 'boolean') return parsed;
        }
      } catch {}
    }
    return true;
  });

  useEffect(() => {
    let isMounted = true;
    const syncGlobalDelivery = async () => {
      try {
        const res = await fetch(getApiUrl('/api/delivery/status'), { cache: 'no-store' });
        const d = await res.json().catch(() => null);
        if (res.ok && d && typeof d.enabled === 'boolean') {
          if (isMounted) setGlobalDeliveryEnabled(d.enabled);
          try {
            localStorage.setItem('leton_global_delivery_enabled', JSON.stringify(d.enabled));
          } catch {}
          return;
        }
      } catch {}

      try {
        const client = getSupabase();
        const { data: row, error } = await client
          .from('leton_content')
          .select('content')
          .eq('id', 'global_delivery_settings')
          .maybeSingle();
        if (!error && row && row.content) {
          const c = typeof row.content === 'string' ? JSON.parse(row.content) : row.content;
          if (c && typeof c.enabled === 'boolean') {
            if (isMounted) setGlobalDeliveryEnabled(c.enabled);
            try {
              localStorage.setItem('leton_global_delivery_enabled', JSON.stringify(c.enabled));
            } catch {}
          }
        }
      } catch {}
    };

    syncGlobalDelivery();

    const handleGlobalUpdate = (e: Event) => {
      const customEvt = e as CustomEvent<{ enabled?: boolean }>;
      if (customEvt.detail && typeof customEvt.detail.enabled === 'boolean') {
        setGlobalDeliveryEnabled(customEvt.detail.enabled);
      } else {
        syncGlobalDelivery();
      }
    };

    window.addEventListener('leton_global_delivery_updated', handleGlobalUpdate);
    return () => {
      isMounted = false;
      window.removeEventListener('leton_global_delivery_updated', handleGlobalUpdate);
    };
  }, []);

  const isDeliverySupported = outlet.id === 'sudirman' && isOutletDeliveryCapable && globalDeliveryEnabled;
  const [orderType, setOrderType] = useState<OrderType>(() => {
    if (initialOrderType === 'DELIVERY' && isDeliverySupported) {
      return 'DELIVERY';
    }
    return 'TAKE AWAY';
  });

  // Sync adaptive order type if initialOrderType, outlet delivery support, or global delivery status changes
  useEffect(() => {
    if (!isDeliverySupported && orderType === 'DELIVERY') {
      setOrderType('TAKE AWAY');
      return;
    }
    if (initialOrderType === 'DELIVERY') {
      if (isDeliverySupported) {
        setOrderType('DELIVERY');
      } else {
        setOrderType('TAKE AWAY');
      }
    } else if (initialOrderType) {
      setOrderType(initialOrderType);
    }
  }, [initialOrderType, isDeliverySupported, outlet.id]);

  // Force reset orderType to TAKE AWAY immediately if outlet changes to non-Sudirman
  useEffect(() => {
    const normOutlet = (outlet.id || '').toLowerCase().trim();
    if (normOutlet !== 'sudirman') {
      if (orderType === 'DELIVERY') {
        setOrderType('TAKE AWAY');
      }
    }
  }, [outlet.id, orderType]);

  const [deliveryAddress, setDeliveryAddress] = useState<string>('');
  const [customerLat, setCustomerLat] = useState<number | null>(null);
  const [customerLng, setCustomerLng] = useState<number | null>(null);
  const [deliveryDistance, setDeliveryDistance] = useState<number | null>(null);
  const [deliveryFee, setDeliveryFee] = useState<number>(0);
  const [isLocating, setIsLocating] = useState<boolean>(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [isDistanceExceeded, setIsDistanceExceeded] = useState<boolean>(false);

  // Switch between Pick Up and Delivery
  const handleSelectOrderType = (type: OrderType) => {
    const normOutlet = (outlet.id || '').toLowerCase().trim();
    const isSudirmanOutlet = normOutlet === 'sudirman';
    if (type === 'DELIVERY' && (!isSudirmanOutlet || !isDeliverySupported)) {
      return;
    }
    setOrderType(type);
    setFormError('');
  };

  // Geolocation GPS handler
  const handleGetGPSLocation = () => {
    if (typeof window === 'undefined' || !navigator.geolocation) {
      setLocationError('Browser atau perangkat Anda tidak mendukung fitur geolokasi GPS.');
      return;
    }

    setIsLocating(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setIsLocating(false);
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          setLocationError('Koordinat GPS tidak valid. Silakan coba kembali.');
          return;
        }

        setCustomerLat(lat);
        setCustomerLng(lng);

        const outletCoords = (outlet.latitude && outlet.longitude)
          ? { lat: outlet.latitude, lng: outlet.longitude }
          : getOutletCoordinates(outlet.id);

        if (!outletCoords) {
          setLocationError('Koordinat outlet asal belum terkonfigurasi untuk pengantaran.');
          return;
        }

        const distKm = calculateHaversineDistanceKm(
          outletCoords.lat,
          outletCoords.lng,
          lat,
          lng
        );
        setDeliveryDistance(distKm);

        const feeCalc = calculateDeliveryFee(distKm);
        if (!feeCalc.isAllowed) {
          setDeliveryFee(0);
          setIsDistanceExceeded(true);
          setLocationError(feeCalc.error || 'Jarak pengantaran melebihi batas maksimal.');
        } else {
          setDeliveryFee(feeCalc.fee);
          setIsDistanceExceeded(false);
          setLocationError(null);
        }
      },
      (geoError) => {
        setIsLocating(false);
        switch (geoError.code) {
          case geoError.PERMISSION_DENIED:
            setLocationError(
              'Izin akses lokasi GPS ditolak oleh browser/perangkat Anda. Mohon izinkan akses lokasi pada pengaturan browser untuk melanjutkan pesanan Delivery.'
            );
            break;
          case geoError.POSITION_UNAVAILABLE:
            setLocationError(
              'Informasi lokasi GPS tidak tersedia pada perangkat Anda saat ini. Pastikan GPS aktif dan coba kembali.'
            );
            break;
          case geoError.TIMEOUT:
            setLocationError(
              'Waktu pencarian lokasi GPS habis (timeout). Silakan periksa koneksi dan coba beberapa saat lagi.'
            );
            break;
          default:
            setLocationError(
              `Gagal mendeteksi lokasi GPS: ${geoError.message || 'Silakan coba kembali.'}`
            );
            break;
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      }
    );
  };

  // Recalculate distance & fee if outlet or coordinates change after customer GPS is set
  useEffect(() => {
    if (orderType === 'DELIVERY' && customerLat !== null && customerLng !== null) {
      const outletCoords = (outlet.latitude && outlet.longitude)
        ? { lat: outlet.latitude, lng: outlet.longitude }
        : getOutletCoordinates(outlet.id);

      if (outletCoords) {
        const distKm = calculateHaversineDistanceKm(
          outletCoords.lat,
          outletCoords.lng,
          customerLat,
          customerLng
        );
        setDeliveryDistance(distKm);

        const feeCalc = calculateDeliveryFee(distKm);
        if (!feeCalc.isAllowed) {
          setDeliveryFee(0);
          setIsDistanceExceeded(true);
          setLocationError(feeCalc.error || 'Jarak pengantaran melebihi batas maksimal.');
        } else {
          setDeliveryFee(feeCalc.fee);
          setIsDistanceExceeded(false);
          setLocationError(null);
        }
      }
    }
  }, [outlet.id, outlet.latitude, outlet.longitude, customerLat, customerLng, orderType]);

  // Bersihkan error lama saat halaman checkout baru dibuka
  useEffect(() => {
    setFormError('');
  }, [customerProfile?.id, customerProfile?.userId, outlet?.id]);
  const [isOutletOpen, setIsOutletOpen] = useState<boolean>(true);

  // Subscribe to live outlet availability updates
  useEffect(() => {
    const unsub = subscribeToOutletAvailabilityRealtime((statusMap) => {
      const normKey = normalizeOutletKey(outlet?.id);
      setIsOutletOpen(statusMap[normKey] !== false);
    });
    return () => unsub();
  }, [outlet?.id]);

  const [copiedAmount, setCopiedAmount] = useState<boolean>(false);
  const [isQrisVisible, setIsQrisVisible] = useState<boolean>(false);

  // QRIS Receipt Upload State
  const [uploadedReceiptUrl, setUploadedReceiptUrl] = useState<string | null>(null);
  const [uploadedReceiptPath, setUploadedReceiptPath] = useState<string | null>(null);
  const [isUploadingReceipt, setIsUploadingReceipt] = useState<boolean>(false);
  const [receiptUploadError, setReceiptUploadError] = useState<string | null>(null);
  const [isReceiptStatusUncertain, setIsReceiptStatusUncertain] = useState<boolean>(false);
  const [isBackgroundUploadPending, setIsBackgroundUploadPending] = useState<boolean>(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [uploadedFileSize, setUploadedFileSize] = useState<string | null>(null);
  const uploadInFlightRef = useRef<boolean>(false);
  const uploadAttemptIdRef = useRef<number>(0);
  const uploadAbortControllerRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef<boolean>(true);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const isSubmittingRef = useRef<boolean>(false);

  // Sync customerProfile changes to name & phone if initially empty
  useEffect(() => {
    if (customerProfile?.namaLengkap && !customerName) {
      setCustomerName(customerProfile.namaLengkap);
    }
    if (customerProfile?.nomorHp && !customerPhone) {
      setCustomerPhone(customerProfile.nomorHp);
    }
  }, [customerProfile?.namaLengkap, customerProfile?.nomorHp]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      uploadAttemptIdRef.current += 1;
      uploadInFlightRef.current = false;
      if (uploadAbortControllerRef.current) {
        uploadAbortControllerRef.current.abort();
        uploadAbortControllerRef.current = null;
      }
    };
  }, []);

  // Timer countdown
  const [timeLeft, setTimeLeft] = useState<number>(15 * 60);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Voucher State
  const [availableVouchers, setAvailableVouchers] = useState<RewardRedemption[]>([]);
  const [allRedemptions, setAllRedemptions] = useState<RewardRedemption[]>([]);
  const [isLoadingVouchers, setIsLoadingVouchers] = useState<boolean>(false);
  const [selectedVoucher, setSelectedVoucher] = useState<RewardRedemption | null>(null);
  // Bersihkan error form voucher jika pilihan voucher dihapus atau diubah
  useEffect(() => {
    if (!selectedVoucher && formError && (formError.toLowerCase().includes("voucher") || formError.toLowerCase().includes("digunakan") || formError.toLowerCase().includes("tidak aktif"))) {
      setFormError("");
    }
  }, [selectedVoucher, formError]);

  const [isVoucherModalOpen, setIsVoucherModalOpen] = useState<boolean>(false);

  // Fetch active vouchers for logged in customer
  useEffect(() => {
    const custId = customerProfile?.id || customerProfile?.userId;
    if (!custId) {
      setAvailableVouchers([]);
      setSelectedVoucher(null);
      return;
    }

    let isMounted = true;
    setIsLoadingVouchers(true);
    getRewardRedemptions(custId)
      .then((redemptions) => {
        if (!isMounted) return;
        const all = redemptions || [];
        setAllRedemptions(all);
        const active = all.filter((r) => isVoucherEligibleForCheckout(r));
        setAvailableVouchers(active);
        setSelectedVoucher((prev) => {
          if (!prev) return null;
          const stillActive = active.some((v) => v.id === prev.id);
          return stillActive ? prev : null;
        });
      })
      .catch((err) => {
        console.warn('[OrderCheckout] Error fetching customer vouchers:', err);
      })
      .finally(() => {
        if (isMounted) setIsLoadingVouchers(false);
      });

    return () => {
      isMounted = false;
    };
  }, [customerProfile?.id, customerProfile?.userId]);

  const subtotal = cart.reduce(
    (acc, item) =>
      acc + calculateItemUnitPrice(item.product.price, item.size, item.topping, item.syrup, item.customOptions) * item.quantity,
    0
  );
  const totalItems = cart.reduce((acc, item) => acc + item.quantity, 0);

  // Voucher Discount Calculation
  const voucherDiscount = useMemo(() => {
    if (!selectedVoucher) return 0;
    if (selectedVoucher.rewardType === 'DISCOUNT_NOMINAL') {
      return Math.min(Number(selectedVoucher.rewardValue || 0), subtotal);
    }
    if (selectedVoucher.rewardType === 'DISCOUNT_PERCENT') {
      const pct = Number(selectedVoucher.rewardValue || 0);
      return Math.min(Math.floor((subtotal * pct) / 100), subtotal);
    }
    // FREE_ITEM: not yet enabled for online checkout calculation
    return 0;
  }, [selectedVoucher, subtotal]);

  const currentDeliveryFee = orderType === 'DELIVERY' ? deliveryFee : 0;
  const serviceFee = subtotal > 0 ? FIXED_SERVICE_FEE : 0;
  const grandTotal = Math.max(0, subtotal - voucherDiscount) + currentDeliveryFee + serviceFee;

  // Reset savedOrderRef if cart, grandTotal, or paymentMethod changes (prevents stale order ID reuse across different carts)
  useEffect(() => {
    setSavedOrderRef(null);
  }, [cart, grandTotal, paymentMethod]);

  const handleCopyAmount = () => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(grandTotal.toString());
      setCopiedAmount(true);
      setTimeout(() => setCopiedAmount(false), 2000);
    }
  };

  // Quick note chips
  const quickNotes = [
    'Pisahkan es batu',
    'Less sugar (50%)',
    'Sedotan ramah lingkungan',
    'Label nama di cup',
  ];

  const handleAddQuickNote = (note: string) => {
    setOrderNote((prev) => (prev ? `${prev}, ${note}` : note));
  };

  // File upload handler for QRIS
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputEl = e.currentTarget;
    const file = inputEl.files?.[0];
    // Reset input value immediately so selecting the same file again on iOS Safari triggers onChange
    inputEl.value = '';
    if (!file) return;

    // Prevent concurrent uploads or starting a new attempt while previous attempt is still active or its outcome is uncertain
    if (
      uploadInFlightRef.current ||
      isUploadingReceipt ||
      isBackgroundUploadPending ||
      isReceiptStatusUncertain
    ) {
      return;
    }

    const ext = (file.name.split('.').pop() || '').toLowerCase();
    const validExtensions = ['jpg', 'jpeg', 'png', 'webp'];
    const validMimes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validExtensions.includes(ext) && !validMimes.includes(file.type)) {
      setUploadedReceiptUrl(null);
      setUploadedReceiptPath(null);
      setIsReceiptStatusUncertain(false);
      setReceiptUploadError('Format file tidak didukung. Gunakan JPG, JPEG, PNG, atau WEBP.');
      return;
    }

    if (file.size <= 0) {
      setUploadedReceiptUrl(null);
      setUploadedReceiptPath(null);
      setIsReceiptStatusUncertain(false);
      setReceiptUploadError('File bukti pembayaran kosong atau tidak valid.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setUploadedReceiptUrl(null);
      setUploadedReceiptPath(null);
      setIsReceiptStatusUncertain(false);
      setReceiptUploadError('Ukuran file maksimal 5 MB.');
      return;
    }

    const currentAttemptId = ++uploadAttemptIdRef.current;
    const abortController = new AbortController();
    uploadAbortControllerRef.current = abortController;

    uploadInFlightRef.current = true;
    setIsUploadingReceipt(true);
    setReceiptUploadError(null);
    setIsReceiptStatusUncertain(false);
    setFormError('');
    setUploadedReceiptUrl(null);
    setUploadedReceiptPath(null);
    setUploadedFileName(file.name);
    setUploadedFileSize(
      file.size >= 1024 * 1024
        ? `${(file.size / (1024 * 1024)).toFixed(2)} MB`
        : `${Math.max(1, Math.round(file.size / 1024))} KB`
    );

    let safetyTimeoutId: ReturnType<typeof setTimeout> | null = null;
    try {
      const tempOrderRef = `ORD-${Date.now().toString().slice(-6)}`;
      const uploadPromise = uploadPaymentReceipt(file, tempOrderRef, {
        signal: abortController.signal,
        timeoutMs: TOTAL_RECEIPT_UPLOAD_TIMEOUT_MS,
        onBackgroundPendingChange: (pending) => {
          if (isMountedRef.current && uploadAttemptIdRef.current === currentAttemptId) {
            setIsBackgroundUploadPending(pending);
          }
        },
      });
      const timeoutPromise = new Promise<ReceiptUploadResult>((resolve) => {
        safetyTimeoutId = setTimeout(() => {
          abortController.abort();
          resolve({
            success: false,
            uncertain: true,
            error:
              'Waktu upload melewati batas 60 detik dan status penyimpanan bukti belum dapat dipastikan. Silakan ketuk "Atur Ulang & Pilih File" untuk mencoba kembali.',
          });
        }, TOTAL_RECEIPT_UPLOAD_TIMEOUT_MS);
      });

      const result = await Promise.race([uploadPromise, timeoutPromise]);
      if (!isMountedRef.current || uploadAttemptIdRef.current !== currentAttemptId) {
        return;
      }

      if (result.success && result.url && result.path) {
        setUploadedReceiptUrl(result.url);
        setUploadedReceiptPath(result.path);
        setIsReceiptStatusUncertain(false);
        setReceiptUploadError(null);
      } else {
        setUploadedReceiptUrl(null);
        setUploadedReceiptPath(null);
        setIsReceiptStatusUncertain(Boolean(result.uncertain));
        setReceiptUploadError(
          result.error || 'Gagal mengupload bukti pembayaran. Silakan coba pilih file lagi.'
        );
      }
    } catch {
      if (!isMountedRef.current || uploadAttemptIdRef.current !== currentAttemptId) {
        return;
      }
      setUploadedReceiptUrl(null);
      setUploadedReceiptPath(null);
      setIsReceiptStatusUncertain(false);
      setReceiptUploadError('Terjadi kendala jaringan saat mengupload. Silakan coba lagi.');
    } finally {
      if (safetyTimeoutId) clearTimeout(safetyTimeoutId);
      if (isMountedRef.current && uploadAttemptIdRef.current === currentAttemptId) {
        uploadAbortControllerRef.current = null;
        uploadInFlightRef.current = false;
        setIsUploadingReceipt(false);
      }
    }
  };

  const handleClearReceipt = () => {
    if (uploadInFlightRef.current || isUploadingReceipt || isBackgroundUploadPending) return;
    uploadAttemptIdRef.current += 1;
    setUploadedReceiptUrl(null);
    setUploadedReceiptPath(null);
    setReceiptUploadError(null);
    setIsReceiptStatusUncertain(false);
    setUploadedFileName(null);
    setUploadedFileSize(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleResetUncertainReceipt = () => {
    if (isUploadingReceipt) return;
    uploadAttemptIdRef.current += 1;
    if (uploadAbortControllerRef.current) {
      uploadAbortControllerRef.current.abort();
      uploadAbortControllerRef.current = null;
    }
    uploadInFlightRef.current = false;
    setIsBackgroundUploadPending(false);
    setIsReceiptStatusUncertain(false);
    setReceiptUploadError(null);
    setUploadedReceiptUrl(null);
    setUploadedReceiptPath(null);
    setUploadedFileName(null);
    setUploadedFileSize(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const scrollToFormError = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const scrollableModal = document.querySelector('.overflow-y-auto');
    if (scrollableModal) {
      scrollableModal.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if (isSubmitting || isInitiatingDana || isSubmittingRef.current) {
      console.warn('[OrderCheckout] Submit ignored: already submitting');
      return;
    }
    isSubmittingRef.current = true;
    setFormError('');

    // Outlet Stock Validation
    const unavailableItem = (cart || []).find(
      (it) => it?.product && !isMenuItemAvailableForOutlet(it.product, outlet?.id)
    );
    if (unavailableItem) {
      setFormError(
        `Menu "${unavailableItem.product.name}" saat ini sedang HABIS di cabang ${outlet?.shortName || outlet?.name || 'ini'}. Silakan kembali ke keranjang untuk menghapusnya.`
      );
      scrollToFormError();
      isSubmittingRef.current = false;
      return;
    }

    if (!isOutletOpen) {
      setFormError('Cabang ini sedang tidak menerima pesanan online saat ini. Silakan pilih cabang lain.');
      scrollToFormError();
      isSubmittingRef.current = false;
      return;
    }

    if (!customerName.trim()) {
      setFormError('Nama pemesan wajib diisi.');
      scrollToFormError();
      isSubmittingRef.current = false;
      return;
    }

    if (paymentMethod !== 'QRIS' && paymentMethod !== 'DANA') {
      setFormError('Metode pembayaran tunai sudah tidak tersedia. Silakan pilih QRIS atau DANA.');
      scrollToFormError();
      isSubmittingRef.current = false;
      return;
    }

    if (paymentMethod === 'DANA') {
      if (!isDanaEnabled) {
        setFormError('Metode pembayaran DANA sedang dinonaktifkan sementara. Silakan pilih metode pembayaran lain.');
        scrollToFormError();
        isSubmittingRef.current = false;
        return;
      }
      if (grandTotal < danaMinAmount) {
        setFormError(`Batas minimum pembayaran dengan DANA adalah ${formatRupiah(danaMinAmount)}.`);
        scrollToFormError();
        isSubmittingRef.current = false;
        return;
      }
      if (grandTotal > danaMaxAmount) {
        setFormError(`Batas maksimum pembayaran dengan DANA adalah ${formatRupiah(danaMaxAmount)}.`);
        scrollToFormError();
        isSubmittingRef.current = false;
        return;
      }
    }

    if (
      paymentMethod === 'QRIS' &&
      (isUploadingReceipt || isReceiptStatusUncertain || !uploadedReceiptUrl || !uploadedReceiptPath)
    ) {
      setIsQrisVisible(true);
      setFormError('Bukti transfer / screenshot pembayaran QRIS wajib berhasil diunggah sebelum melanjutkan.');
      isSubmittingRef.current = false;
      setTimeout(() => {
        const uploadEl = document.querySelector('input[type="file"]')?.parentElement;
        if (uploadEl) {
          uploadEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }

    if (orderType === 'DELIVERY') {
      if (!isDeliverySupported) {
        setFormError(`Layanan Delivery tidak tersedia untuk cabang ${outlet.name}. Silakan pilih Self Pick-Up.`);
        scrollToFormError();
        isSubmittingRef.current = false;
        return;
      }
      if (!deliveryAddress.trim()) {
        setFormError('Alamat lengkap pengantaran wajib diisi untuk pesanan Delivery.');
        scrollToFormError();
        isSubmittingRef.current = false;
        return;
      }
      if (customerLat === null || customerLng === null) {
        setFormError('Lokasi GPS pengantaran belum terdeteksi. Silakan klik tombol "Gunakan Lokasi Saya (GPS)" untuk menghitung jarak dan tarif ongkir resmi.');
        scrollToFormError();
        isSubmittingRef.current = false;
        return;
      }
      if (isDistanceExceeded || (deliveryDistance !== null && deliveryDistance > 10)) {
        setFormError(`Jarak pengantaran (${deliveryDistance !== null ? deliveryDistance.toFixed(1) : ''} km) melebihi batas maksimal 10 km. Silakan pilih opsi Self Pick-Up.`);
        scrollToFormError();
        isSubmittingRef.current = false;
        return;
      }
      if (deliveryFee <= 0) {
        setFormError('Ongkos kirim belum berhasil dihitung. Silakan ulangi deteksi GPS.');
        scrollToFormError();
        isSubmittingRef.current = false;
        return;
      }
    }

    try {
      let targetRef = savedOrderRef;

      if (paymentMethod === 'DANA' && !targetRef) {
        const res = await onSubmitOrder({
          customerName: customerName.trim(),
          customerPhone: customerPhone.trim(),
          orderType,
          tableNumber: '',
          paymentMethod,
          paymentReceiptUrl: undefined,
          paymentReceiptPath: undefined,
          isMemberChoice,
          pickupTime: orderType === 'TAKE AWAY' ? pickupTime.trim() : '',
          voucherId: selectedVoucher ? selectedVoucher.id : undefined,
          voucherCode: selectedVoucher ? (selectedVoucher.voucherCode || undefined) : undefined,
          discountAmount: voucherDiscount > 0 ? voucherDiscount : undefined,
          deliveryAddress: orderType === 'DELIVERY' ? deliveryAddress.trim() : undefined,
          customerLat: orderType === 'DELIVERY' ? (customerLat ?? undefined) : undefined,
          customerLng: orderType === 'DELIVERY' ? (customerLng ?? undefined) : undefined,
          deliveryDistance: orderType === 'DELIVERY' ? (deliveryDistance ?? undefined) : undefined,
          deliveryFee: orderType === 'DELIVERY' ? deliveryFee : undefined,
        });

        if (res && res.success === false) {
          const errText = (res.error || '').toLowerCase();
          if (
            errText.includes('voucher') ||
            errText.includes('digunakan') ||
            errText.includes('tidak aktif') ||
            errText.includes('kedaluwarsa') ||
            errText.includes('tidak ditemukan')
          ) {
            setSelectedVoucher(null);
            const custId = customerProfile?.id || customerProfile?.userId;
            if (custId) {
              getRewardRedemptions(custId)
                .then((redemptions) => {
                  if (!isMountedRef.current) return;
                  const all = redemptions || [];
                  setAllRedemptions(all);
                  const active = all.filter((r) => isVoucherEligibleForCheckout(r));
                  setAvailableVouchers(active);
                })
                .catch(() => {});
            }
          }
          setFormError(res.error || 'Gagal menyimpan pesanan. Silakan coba beberapa saat lagi.');
          scrollToFormError();
          isSubmittingRef.current = false;
          return;
        }

        const createdOrder = res && (res as any).order;
        targetRef = createdOrder?.orderNumber || createdOrder?.id;
        if (targetRef) {
          setSavedOrderRef(targetRef);
        }
      } else if (paymentMethod !== 'DANA') {
        const res = await onSubmitOrder({
          customerName: customerName.trim(),
          customerPhone: customerPhone.trim(),
          orderType,
          tableNumber: '',
          paymentMethod,
          paymentReceiptUrl: paymentMethod === 'QRIS' ? (uploadedReceiptUrl || undefined) : undefined,
          paymentReceiptPath: paymentMethod === 'QRIS' ? (uploadedReceiptPath || undefined) : undefined,
          isMemberChoice,
          pickupTime: orderType === 'TAKE AWAY' ? pickupTime.trim() : '',
          voucherId: selectedVoucher ? selectedVoucher.id : undefined,
          voucherCode: selectedVoucher ? (selectedVoucher.voucherCode || undefined) : undefined,
          discountAmount: voucherDiscount > 0 ? voucherDiscount : undefined,
          deliveryAddress: orderType === 'DELIVERY' ? deliveryAddress.trim() : undefined,
          customerLat: orderType === 'DELIVERY' ? (customerLat ?? undefined) : undefined,
          customerLng: orderType === 'DELIVERY' ? (customerLng ?? undefined) : undefined,
          deliveryDistance: orderType === 'DELIVERY' ? (deliveryDistance ?? undefined) : undefined,
          deliveryFee: orderType === 'DELIVERY' ? deliveryFee : undefined,
        });

        if (res && res.success === false) {
          const errText = (res.error || '').toLowerCase();
          if (
            errText.includes('voucher') ||
            errText.includes('digunakan') ||
            errText.includes('tidak aktif') ||
            errText.includes('kedaluwarsa') ||
            errText.includes('tidak ditemukan')
          ) {
            setSelectedVoucher(null);
            const custId = customerProfile?.id || customerProfile?.userId;
            if (custId) {
              getRewardRedemptions(custId)
                .then((redemptions) => {
                  if (!isMountedRef.current) return;
                  const all = redemptions || [];
                  setAllRedemptions(all);
                  const active = all.filter((r) => isVoucherEligibleForCheckout(r));
                  setAvailableVouchers(active);
                })
                .catch(() => {});
            }
          }
          setFormError(res.error || 'Gagal menyimpan pesanan. Silakan coba beberapa saat lagi.');
          scrollToFormError();
          return;
        }
      }

      // If DANA payment method, initiate Hosted Checkout redirect with server
      if (paymentMethod === 'DANA') {
        if (!targetRef) {
          setFormError('Pesanan tersimpan namun ID pesanan DANA tidak ditemukan. Silakan hubungi kasir.');
          scrollToFormError();
          isSubmittingRef.current = false;
          return;
        }

        setIsInitiatingDana(true);
        try {
          const createRes = await fetch('/api/payment/dana/create-order', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ orderId: targetRef }),
          });

          const createData = await createRes.json().catch(() => ({}));

          if (
            createRes.ok &&
            createData?.success &&
            typeof createData.webRedirectUrl === 'string' &&
            createData.webRedirectUrl.startsWith('http')
          ) {
            setSelectedVoucher(null);
            onClearCart?.();
            // Redirect customer directly to official DANA Hosted Checkout
            window.location.href = createData.webRedirectUrl;
            return;
          } else {
            const errorMsg =
              createData?.error ||
              'Layanan pembayaran DANA sedang tidak dapat memproses transaksi saat ini. Silakan konfirmasi ke kasir.';
            setFormError(errorMsg);
            scrollToFormError();
          }
        } catch (fetchErr: any) {
          console.error('[OrderCheckout] DANA create-order error:', fetchErr);
          setFormError('Koneksi ke gateway DANA terputus. Silakan coba beberapa saat lagi atau hubungi kasir.');
          scrollToFormError();
        } finally {
          setIsInitiatingDana(false);
          isSubmittingRef.current = false;
        }
        return;
      }

      setSelectedVoucher(null);
    } catch (err: any) {
      console.error('[OrderCheckout] Order submit error:', err);
      const errText = (err?.message || '').toLowerCase();
      if (
        errText.includes('voucher') ||
        errText.includes('digunakan') ||
        errText.includes('tidak aktif') ||
        errText.includes('kedaluwarsa') ||
        errText.includes('tidak ditemukan')
      ) {
        setSelectedVoucher(null);
        const custId = customerProfile?.id || customerProfile?.userId;
        if (custId) {
          getRewardRedemptions(custId)
            .then((redemptions) => {
              if (!isMountedRef.current) return;
              const now = Date.now();
              const all = redemptions || [];
              setAllRedemptions(all);
              const active = all.filter((r) => isVoucherEligibleForCheckout(r));
              setAvailableVouchers(active);
            })
            .catch(() => {});
        }
      }
      setFormError(err?.message || 'Terjadi kendala saat memproses pesanan. Silakan coba lagi.');
      scrollToFormError();
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const isPlaceOrderDisabled =
    !isOutletOpen ||
    isSubmitting ||
    isInitiatingDana ||
    (paymentMethod === 'QRIS' && (isUploadingReceipt || isReceiptStatusUncertain)) ||
    (orderType === 'DELIVERY' && (
      !deliveryAddress.trim() ||
      customerLat === null ||
      customerLng === null ||
      isDistanceExceeded ||
      isLocating ||
      deliveryFee <= 0
    ));

  return (
    <div className="bg-[#F8FAFC] text-[#0A192F] font-sans antialiased min-h-screen pb-36">
      <header 
        className="sticky top-0 z-30 bg-white px-4 pb-3 border-b border-[#E2E8F0] flex items-center justify-between shadow-2xs"
        style={{ paddingTop: 'calc(1rem + env(safe-area-inset-top, 0px))' }}
      >
        <button
          type="button"
          onClick={onBackToCart}
          className="w-10 h-10 rounded-full bg-slate-50 border border-[#E2E8F0] flex items-center justify-center text-[#0A192F] hover:bg-slate-100 transition-colors cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h1 className="text-lg font-bold text-[#0A192F]">Checkout</h1>
        <div className="w-10" />
      </header>

      <main className="max-w-lg mx-auto px-4 py-4 space-y-4">
        {/* Closed Outlet Banner Alert */}
        {!isOutletOpen && (
          <div className="p-3.5 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 shadow-2xs flex items-start gap-2.5">
            <AlertCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
            <div className="flex-1 text-xs">
              <h4 className="font-bold text-rose-900">Penerimaan Pesanan Tutup</h4>
              <p className="mt-0.5 text-rose-700">
                Cabang <strong>{outlet.name}</strong> saat ini sedang <strong>TIDAK MENERIMA PESANAN ONLINE</strong>.
              </p>
            </div>
          </div>
        )}

        {/* Active Promo Notice Banner */}
        {activePromo && (
          <div className="bg-gradient-to-r from-[#0056D2] via-[#0046AB] to-[#003A99] text-white p-3.5 sm:p-4 rounded-2xl shadow-sm border border-blue-400/30 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-white/20 backdrop-blur-xs flex items-center justify-center shrink-0 shadow-xs">
                <Tag className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0">
                <span className="text-[10px] uppercase font-bold tracking-wider text-blue-200 block">
                  Paket Promo Dipilih
                </span>
                <h4 className="font-bold text-xs sm:text-sm text-white truncate">
                  {activePromo.title}
                </h4>
                {activePromo.discount_info && (
                  <p className="text-[11px] text-blue-100 truncate mt-0.5">
                    {activePromo.discount_info}
                  </p>
                )}
              </div>
            </div>
            {activePromo.badge_text && (
              <span className="px-2.5 py-1 rounded-lg bg-white text-[#0056D2] text-[10px] font-extrabold uppercase shrink-0 shadow-xs">
                {activePromo.badge_text}
              </span>
            )}
          </div>
        )}

        <form id="checkout-order-form" onSubmit={handleSubmit} className="space-y-4">
          {/* Order Type Toggle (UX Pilot Screen 6) */}
          <section className="bg-white p-1.5 rounded-xl border border-[#E2E8F0] flex gap-1.5 shadow-2xs">
            <button
              type="button"
              onClick={() => handleSelectOrderType('TAKE AWAY')}
              className={`flex-1 py-2.5 text-xs sm:text-sm font-bold rounded-lg flex items-center justify-center gap-2 cursor-pointer transition-all ${
                orderType === 'TAKE AWAY'
                  ? 'bg-[#0A192F] text-white shadow-xs'
                  : 'text-[#64748B] hover:text-[#0A192F] hover:bg-slate-50'
              }`}
            >
              <Store className="w-4 h-4" />
              <span>Self Pick-Up</span>
            </button>
            <button
              type="button"
              onClick={() => handleSelectOrderType('DELIVERY')}
              disabled={!isDeliverySupported}
              title={!isDeliverySupported ? 'Layanan Delivery tidak tersedia untuk cabang ini' : 'Pesan Antar ke Alamat'}
              className={`flex-1 py-2.5 text-xs sm:text-sm font-bold rounded-lg flex items-center justify-center gap-2 transition-all ${
                !isDeliverySupported
                  ? 'bg-slate-50 text-slate-300 opacity-60 cursor-not-allowed'
                  : orderType === 'DELIVERY'
                  ? 'bg-[#0A192F] text-white shadow-xs cursor-pointer'
                  : 'text-[#64748B] hover:text-[#0A192F] hover:bg-slate-50 cursor-pointer'
              }`}
            >
              <Truck className="w-4 h-4" />
              <span>Delivery</span>
              {!isDeliverySupported && (
                <span className="text-[9px] uppercase px-1.5 py-0.2 bg-slate-200 text-slate-500 rounded font-semibold ml-1">
                  Off
                </span>
              )}
            </button>
          </section>

          {/* Delivery Outlet / Global Support Alert */}
          {!isDeliverySupported && (
            <div className="p-2.5 rounded-xl bg-slate-100 border border-slate-200 text-slate-600 text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-slate-500 shrink-0" />
              <span>
                {!globalDeliveryEnabled
                  ? 'Layanan pengantaran (Delivery) saat ini sedang dinonaktifkan sementara untuk seluruh cabang. Silakan gunakan metode Self Pick-Up.'
                  : `Cabang ${outlet.shortName || outlet.name} hanya melayani Self Pick-Up. Layanan Delivery tersedia di cabang Sudirman & Kelakap 7.`}
              </span>
            </div>
          )}

          {/* Conditional: Pickup Details (when Self Pick-Up selected) */}
          {orderType === 'TAKE AWAY' && (
            <section className="bg-white rounded-2xl p-4 border border-[#E2E8F0] shadow-2xs space-y-4">
              <div className="flex items-start justify-between">
                <div className="flex gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 text-[#0056D2] flex items-center justify-center shrink-0 mt-0.5">
                    <Store className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-[#0056D2] bg-blue-50 px-2 py-0.5 rounded-md">
                      Pick-Up Location
                    </span>
                    <h3 className="font-bold text-sm text-[#0A192F] mt-1">{outlet.name}</h3>
                    <p className="text-xs text-[#64748B] mt-0.5 leading-relaxed">
                      {outlet.address || 'Jl. Jend. Sudirman, Dumai'}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onBackToCart}
                  className="text-xs font-bold text-[#0056D2] hover:underline cursor-pointer shrink-0"
                >
                  Change
                </button>
              </div>

              <div className="pt-3 border-t border-slate-100 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <Bolt className="w-4 h-4 text-[#64748B]" />
                  <span className="text-xs font-medium text-[#0A192F]">Estimated Pick-Up Time</span>
                </div>
                <input
                  id="input-pickup-time"
                  type="text"
                  value={pickupTime}
                  onChange={(e) => setPickupTime(e.target.value)}
                  placeholder="Ready in 10-15 mins"
                  className="text-right text-base sm:text-xs font-bold text-[#0056D2] bg-blue-50/60 border border-blue-100 rounded-lg px-2.5 py-1.5 w-40 focus:outline-none focus:border-[#0056D2]"
                />
              </div>
            </section>
          )}

          {/* Conditional: Delivery Details (when Delivery selected) */}
          {orderType === 'DELIVERY' && (
            <section className="bg-white rounded-2xl p-4 border border-[#E2E8F0] shadow-2xs space-y-4">
              <div className="flex items-start justify-between pb-3 border-b border-slate-100">
                <div className="flex gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 text-[#0056D2] flex items-center justify-center shrink-0 mt-0.5">
                    <Truck className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-[#0056D2] bg-blue-50 px-2 py-0.5 rounded-md">
                      Pengantaran (Delivery)
                    </span>
                    <h3 className="font-bold text-sm text-[#0A192F] mt-1">Dikirim dari {outlet.name}</h3>
                    <p className="text-xs text-[#64748B] mt-0.5 leading-relaxed">
                      Radius maks. 15 km • Tarif terverifikasi server
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onBackToCart}
                  className="text-xs font-bold text-[#0056D2] hover:underline cursor-pointer shrink-0"
                >
                  Ganti Cabang
                </button>
              </div>

              {/* Delivery Address Input */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold text-[#64748B] block">
                  Alamat Lengkap Pengantaran <span className="text-rose-500">*</span>
                </label>
                <textarea
                  id="input-delivery-address"
                  rows={2}
                  required
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                  placeholder="Nama jalan, nomor rumah / gedung, RT/RW, dan patokan..."
                  className="w-full px-3 py-2 bg-slate-50 border border-[#E2E8F0] rounded-xl text-xs text-[#0A192F] focus:outline-none focus:border-[#0056D2] resize-none"
                />
              </div>

              {/* GPS Geolocation Action */}
              <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-1.5">
                      <MapPin className="w-4 h-4 text-[#0056D2]" />
                      <h4 className="text-xs font-bold text-[#0A192F]">Lokasi GPS Pengantaran</h4>
                      <span className="text-[10px] text-rose-500 font-bold">*Wajib</span>
                    </div>
                    <p className="text-[11px] text-[#64748B] mt-0.5 leading-relaxed">
                      Diperlukan untuk menghitung jarak akurat dan tarif ongkir resmi dari cabang Leton.
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleGetGPSLocation}
                  disabled={isLocating}
                  className={`w-full py-2.5 px-3 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition-all cursor-pointer ${
                    isLocating
                      ? 'bg-blue-100 text-[#0056D2] cursor-wait'
                      : customerLat !== null && customerLng !== null
                      ? 'bg-white border border-[#0056D2] text-[#0056D2] hover:bg-blue-50'
                      : 'bg-[#0056D2] hover:bg-blue-700 text-white shadow-xs'
                  }`}
                >
                  {isLocating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Mendeteksi Lokasi GPS...</span>
                    </>
                  ) : customerLat !== null && customerLng !== null ? (
                    <>
                      <Navigation className="w-4 h-4" />
                      <span>Perbarui Lokasi Saya (GPS)</span>
                    </>
                  ) : (
                    <>
                      <Navigation className="w-4 h-4" />
                      <span>Gunakan Lokasi Saya (GPS)</span>
                    </>
                  )}
                </button>

                {/* GPS Status Cards */}
                {customerLat !== null && customerLng !== null && !isDistanceExceeded && (
                  <div className="p-2.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900 text-xs space-y-1">
                    <div className="flex items-center justify-between font-semibold">
                      <span className="flex items-center gap-1.5 text-emerald-700">
                        <Check className="w-3.5 h-3.5" />
                        Lokasi GPS Terverifikasi
                      </span>
                      <span className="font-mono text-[11px] text-emerald-800">
                        {customerLat.toFixed(5)}, {customerLng.toFixed(5)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-1 border-t border-emerald-200/60 text-[11px]">
                      <span>Jarak dari {outlet.shortName || 'Outlet'}:</span>
                      <span className="font-bold text-[#0A192F]">
                        {deliveryDistance !== null ? `${deliveryDistance.toFixed(2)} km` : '-'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-[11px]">
                      <span>Estimasi Ongkos Kirim:</span>
                      <span className="font-extrabold text-[#0056D2]">
                        {formatRupiah(deliveryFee)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Distance Exceeded Warning (> 10 km) */}
                {isDistanceExceeded && deliveryDistance !== null && (
                  <div className="p-2.5 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-xs space-y-1">
                    <div className="flex items-center gap-1.5 font-bold text-rose-700">
                      <AlertCircle className="w-4 h-4 shrink-0" />
                      <span>Jarak Pengantaran Melebihi Batas (Maksimal 10 km)</span>
                    </div>
                    <p className="text-[11px] leading-relaxed text-rose-700">
                      Jarak lokasi Anda ke cabang {outlet.name} adalah <strong>{deliveryDistance.toFixed(1)} km</strong>. Layanan Delivery LetonKu hanya dapat melayani radius maksimal 10 km.
                    </p>
                    <p className="text-[11px] font-semibold text-rose-900 pt-0.5">
                      Silakan pilih opsi <strong>Self Pick-Up</strong> atau pesan dari cabang Leton terdekat lainnya.
                    </p>
                  </div>
                )}

                {/* GPS Geolocation Error Alert */}
                {locationError && !isDistanceExceeded && (
                  <div className="p-2.5 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <span className="font-bold block">Gagal Mendapatkan Lokasi GPS</span>
                      <span className="text-[11px] leading-relaxed block mt-0.5">{locationError}</span>
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Customer Information */}
          <section className="bg-white rounded-2xl p-4 border border-[#E2E8F0] shadow-2xs space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-sm text-[#0A192F]">Customer Information</h3>
              {customerProfile && (
                <span className="text-[10px] font-bold text-[#0056D2] bg-blue-50 px-2 py-0.5 rounded-md">
                  Member Verified
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-semibold text-[#64748B] block mb-1">
                  Full Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  disabled={Boolean(customerProfile)}
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Enter your name"
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-[#E2E8F0] text-base sm:text-xs font-medium text-[#0A192F] focus:outline-none focus:border-[#0056D2]"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-[#64748B] block mb-1">
                  WhatsApp Number
                </label>
                <input
                  type="tel"
                  disabled={Boolean(customerProfile)}
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  placeholder="0812-xxxx-xxxx"
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-[#E2E8F0] text-base sm:text-xs font-medium text-[#0A192F] focus:outline-none focus:border-[#0056D2]"
                />
              </div>
            </div>

            {!customerProfile && (
              <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs">
                <span className="text-[#64748B] font-medium">Earn Leton Member Points?</span>
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    onClick={() => setIsMemberChoice(true)}
                    className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-colors cursor-pointer ${
                      isMemberChoice ? 'bg-[#0056D2] text-white' : 'bg-slate-100 text-[#64748B]'
                    }`}
                  >
                    Yes, Member
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsMemberChoice(false)}
                    className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-colors cursor-pointer ${
                      !isMemberChoice ? 'bg-[#0A192F] text-white' : 'bg-slate-100 text-[#64748B]'
                    }`}
                  >
                    Guest
                  </button>
                </div>
              </div>
            )}
          </section>

          {/* Order Summary (UX Pilot Screen 6) */}
          <section className="bg-white rounded-2xl p-4 border border-[#E2E8F0] shadow-2xs space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-sm text-[#0A192F]">Order Summary ({totalItems})</h3>
              <button
                type="button"
                onClick={onBackToCart}
                className="text-xs font-semibold text-[#0056D2] flex items-center gap-1 cursor-pointer hover:underline"
              >
                {activePromo ? (
                  <span>Ganti Outlet</span>
                ) : (
                  <>
                    <Plus className="w-3.5 h-3.5" />
                    <span>Edit Items</span>
                  </>
                )}
              </button>
            </div>

            <div className="space-y-3 pt-1">
              {cart.map((item, idx) => {
                const unitPrice = calculateItemUnitPrice(
                  item.product.price,
                  item.size,
                  item.topping,
                  item.syrup,
                  item.customOptions
                );
                const details = [
                  item.size?.name,
                  item.topping?.name !== 'No Topping' ? item.topping?.name : null,
                  item.syrup?.name !== 'No Syrup' ? item.syrup?.name : null,
                  ...(item.customOptions || []).map((c) => c.optionName),
                ]
                  .filter(Boolean)
                  .join(' • ');

                return (
                  <React.Fragment key={idx}>
                    {idx > 0 && <div className="h-px bg-slate-100 w-full" />}
                    <div className="flex justify-between items-start text-xs">
                      <div className="flex gap-2.5 min-w-0 pr-2">
                        <span className="font-bold text-[#0056D2] bg-blue-50 w-5 h-5 rounded flex items-center justify-center shrink-0">
                          {item.quantity}x
                        </span>
                        <div className="min-w-0">
                          <h4 className="font-bold text-[#0A192F] truncate">{item.product.name}</h4>
                          {details && <p className="text-[#64748B] mt-0.5 truncate">{details}</p>}
                          {item.note && (
                            <p className="text-[11px] text-[#0056D2] italic mt-0.5">"{item.note}"</p>
                          )}
                        </div>
                      </div>
                      <span className="font-bold text-[#0A192F] shrink-0">
                        {formatRupiah(unitPrice * item.quantity)}
                      </span>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>

            {/* Order Note */}
            <div className="pt-3 border-t border-slate-100 space-y-2">
              <input
                type="text"
                value={orderNote}
                onChange={(e) => setOrderNote(e.target.value)}
                placeholder="Add notes for barista (optional)..."
                className="w-full px-3 py-2 bg-slate-50 border border-[#E2E8F0] rounded-xl text-base sm:text-xs text-[#0A192F] focus:outline-none focus:border-[#0056D2]"
              />
              <div className="flex flex-wrap gap-1.5">
                {quickNotes.map((qn) => (
                  <button
                    key={qn}
                    type="button"
                    onClick={() => handleAddQuickNote(qn)}
                    className="px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-blue-50 text-[#0056D2] text-[10px] font-semibold cursor-pointer transition-colors"
                  >
                    + {qn}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Payment Method & QRIS (UX Pilot Screen 6 & Screen 7) */}
          <section className="bg-white rounded-2xl p-4 border border-[#E2E8F0] shadow-2xs space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-sm text-[#0A192F]">Payment Method</h3>
              <div className="inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200/60 text-amber-700 px-2.5 py-1 rounded-full">
                <span className="text-[10px] font-bold font-mono">{formatTime(timeLeft)}</span>
              </div>
            </div>

            {/* Payment Method Selector (QRIS & DANA) */}
            <div className={`grid ${isDanaEnabled ? 'grid-cols-2' : 'grid-cols-1'} gap-2`}>
              <button
                type="button"
                onClick={() => setPaymentMethod('QRIS')}
                className={`flex items-center justify-between p-2.5 sm:p-3 rounded-xl border-2 transition-all cursor-pointer text-left ${
                  paymentMethod === 'QRIS'
                    ? 'border-[#0056D2] bg-blue-50/20 shadow-2xs'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center shrink-0 ${
                    paymentMethod === 'QRIS' ? 'bg-[#0056D2] text-white' : 'bg-slate-100 text-slate-600'
                  }`}>
                    <QrCode className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <span className="font-bold text-xs text-[#0A192F] truncate block">QRIS</span>
                    <span className="text-[10px] text-[#64748B] block truncate">Upload Bukti</span>
                  </div>
                </div>
                <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                  paymentMethod === 'QRIS' ? 'border-[#0056D2]' : 'border-slate-300'
                }`}>
                  {paymentMethod === 'QRIS' && <div className="w-2 h-2 rounded-full bg-[#0056D2]" />}
                </div>
              </button>

              {isDanaEnabled && (
                <button
                  type="button"
                  onClick={() => setPaymentMethod('DANA')}
                  className={`flex items-center justify-between p-2.5 sm:p-3 rounded-xl border-2 transition-all cursor-pointer text-left ${
                    paymentMethod === 'DANA'
                      ? 'border-[#118EEA] bg-sky-50/20 shadow-2xs'
                      : 'border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center shrink-0 font-black text-xs sm:text-sm tracking-tighter ${
                      paymentMethod === 'DANA' ? 'bg-[#118EEA] text-white' : 'bg-slate-100 text-slate-600'
                    }`}>
                      DANA
                    </div>
                    <div className="min-w-0">
                      <span className="font-bold text-xs text-[#0A192F] truncate block">DANA</span>
                      <span className="text-[10px] text-[#118EEA] block truncate">Instant</span>
                    </div>
                  </div>
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                    paymentMethod === 'DANA' ? 'border-[#118EEA]' : 'border-slate-300'
                  }`}>
                    {paymentMethod === 'DANA' && <div className="w-2 h-2 rounded-full bg-[#118EEA]" />}
                  </div>
                </button>
              )}
            </div>

            {/* DANA Gapura Hosted Checkout Info Card */}
            {isDanaEnabled && paymentMethod === 'DANA' && (
              <div className="p-3.5 rounded-xl bg-sky-50/70 border border-sky-200/80 space-y-2 animate-fadeIn">
                <div className="flex items-center justify-between text-xs">
                  <div>
                    <span className="text-[10px] text-sky-800 uppercase tracking-wider font-semibold block">
                      Total Pembayaran DANA
                    </span>
                    <span className="font-extrabold text-base text-[#0A192F]">{formatRupiah(grandTotal)}</span>
                  </div>
                  <span className="text-[10px] font-bold bg-[#118EEA] text-white px-2 py-0.5 rounded-md">
                    SNAP BI
                  </span>
                </div>
                <div className="text-[10px] text-[#64748B] flex items-center justify-between pt-1 border-t border-sky-200/50">
                  <span>Batas Transaksi:</span>
                  <span className="font-medium text-[#172033]">
                    {formatRupiah(danaMinAmount)} – {formatRupiah(danaMaxAmount)}
                  </span>
                </div>
                {grandTotal < danaMinAmount && (
                  <p className="text-[11px] text-rose-600 font-semibold bg-rose-50 p-2 rounded-lg border border-rose-200">
                    Nominal belanja di bawah batas minimum DANA ({formatRupiah(danaMinAmount)}).
                  </p>
                )}
                {grandTotal > danaMaxAmount && (
                  <p className="text-[11px] text-rose-600 font-semibold bg-rose-50 p-2 rounded-lg border border-rose-200">
                    Nominal belanja melebihi batas maksimum DANA ({formatRupiah(danaMaxAmount)}).
                  </p>
                )}
                <p className="text-[11px] text-sky-950/80 leading-relaxed">
                  Setelah menekan tombol di bawah, Anda akan diarahkan langsung ke halaman resmi DANA Gapura Hosted Checkout untuk menyelesaikan pembayaran secara aman dan instan tanpa perlu mengunggah bukti transfer.
                </p>
              </div>
            )}

            {/* QRIS Flow: Copy Nominal, QR Container, & Proof Upload */}
            {paymentMethod === 'QRIS' && (
              <>
                {/* Copy Nominal Row */}
                <div className="p-3 rounded-xl bg-slate-50 border border-[#E2E8F0] flex items-center justify-between text-xs">
                  <div>
                    <span className="text-[10px] text-[#64748B] uppercase tracking-wider font-semibold block">
                      Total Payment
                    </span>
                    <span className="font-extrabold text-base text-[#0A192F]">{formatRupiah(grandTotal)}</span>
                  </div>
                  <button
                    type="button"
                    onClick={handleCopyAmount}
                    className="px-3 py-1.5 rounded-lg bg-white text-[#0056D2] font-bold text-xs border border-[#E2E8F0] flex items-center gap-1.5 cursor-pointer hover:bg-blue-50 transition-colors"
                  >
                    {copiedAmount ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                    <span>{copiedAmount ? 'Copied' : 'Copy Amount'}</span>
                  </button>
                </div>

                {/* Show/Hide QRIS Code Button */}
                <button
                  type="button"
                  onClick={() => setIsQrisVisible(!isQrisVisible)}
                  className="w-full py-3 px-4 rounded-xl bg-[#0A192F] hover:bg-slate-800 text-white font-bold text-xs flex items-center justify-center gap-2 cursor-pointer shadow-xs transition-all"
                >
                  <QrCode className="w-4 h-4" />
                  <span>{isQrisVisible ? 'Hide QRIS Code' : 'Show QRIS Code to Scan'}</span>
                </button>

                {/* UX Pilot Screen 7 QRIS Container when expanded */}
                {isQrisVisible && (
                  <div className="bg-white rounded-3xl p-5 border border-[#E2E8F0] shadow-sm relative overflow-hidden animate-fadeIn">
                    <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-[#0A192F] via-[#0056D2] to-[#0A192F]" />
                    <div className="flex items-center justify-between mb-4 pt-1">
                      <div>
                        <h4 className="font-extrabold text-sm text-[#0A192F]">LETONKU</h4>
                        <p className="text-[10px] text-[#64748B] font-medium">
                          NMID: ID102003921829 • {outlet.shortName?.toUpperCase() || 'DUMAI'}
                        </p>
                      </div>
                      <span className="text-xs font-extrabold tracking-tighter bg-slate-100 px-2.5 py-1 rounded-md text-[#0A192F]">
                        QRIS
                      </span>
                    </div>

                    <div className="bg-slate-50 p-4 rounded-2xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center">
                      <div className="w-52 h-52 bg-white p-3 rounded-xl shadow-xs border border-slate-100 flex items-center justify-center">
                        {activeQrisUrl ? (
                          <img
                            src={resolveMediaUrl(activeQrisUrl)}
                            alt={`QRIS ${outlet.name}`}
                            className="w-full h-full object-contain rounded-lg"
                          />
                        ) : (
                          <QrCode className="w-32 h-32 text-[#0A192F]" />
                        )}
                      </div>
                      <p className="text-[11px] font-semibold text-[#64748B] mt-3">
                        Scan or screenshot QR code to pay with any e-wallet or mobile banking
                      </p>
                    </div>
                  </div>
                )}

                {/* Upload Payment Proof */}
                <div className="pt-2 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-[#0A192F]">Upload Payment Receipt</span>
                <span className="text-[10px] font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-md border border-rose-200">
                  Required
                </span>
              </div>

              {uploadedReceiptUrl && uploadedReceiptPath ? (
                <div className="p-3.5 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <FileCheck className="w-5 h-5 text-emerald-600 shrink-0" />
                    <div className="min-w-0">
                      <span className="text-xs font-bold text-emerald-950 truncate block">
                        {uploadedFileName || 'Payment_Receipt.jpg'}
                      </span>
                      <span className="text-[10px] text-emerald-700 font-semibold block">
                        Uploaded ({uploadedFileSize || 'Ready'})
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleClearReceipt}
                    className="p-1.5 text-slate-400 hover:text-rose-600 rounded-lg hover:bg-white cursor-pointer shrink-0"
                    title="Remove Receipt"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label
                  className={`p-4 rounded-xl bg-slate-50 border-2 border-dashed border-[#E2E8F0] flex flex-col items-center justify-center text-center transition-colors group ${
                    isUploadingReceipt || isReceiptStatusUncertain || isBackgroundUploadPending
                      ? 'opacity-70 cursor-not-allowed'
                      : 'hover:border-[#0056D2] cursor-pointer'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleFileChange}
                    disabled={
                      isUploadingReceipt || isReceiptStatusUncertain || isBackgroundUploadPending
                    }
                    className="hidden"
                  />
                  <UploadCloud className="w-7 h-7 text-[#0056D2] mb-1.5 group-hover:-translate-y-0.5 transition-transform" />
                  <span className="text-xs font-bold text-[#0A192F]">
                    {isUploadingReceipt
                      ? 'Uploading Receipt...'
                      : isReceiptStatusUncertain
                        ? 'Status Upload Belum Pasti'
                        : 'Upload Payment Screenshot'}
                  </span>
                  <span className="text-[10px] text-[#64748B] mt-0.5">
                    {isUploadingReceipt
                      ? 'Mohon tunggu (maks. 60 detik)...'
                      : 'JPG, PNG, or m-Banking Screenshot (Max 5MB)'}
                  </span>
                </label>
              )}

              {receiptUploadError && (
                <div
                  className={`p-3 rounded-xl border text-[11px] font-medium space-y-2 ${
                    isReceiptStatusUncertain
                      ? 'bg-amber-50 border-amber-200 text-amber-900'
                      : 'bg-rose-50 border-rose-200 text-rose-700'
                  }`}
                >
                  <p>{receiptUploadError}</p>
                  {isReceiptStatusUncertain && (
                    <div className="flex items-center justify-between gap-2 pt-1">
                      <span className="text-[10px] text-amber-700">
                        {isBackgroundUploadPending
                          ? 'Menunggu koneksi sebelumnya berhenti...'
                          : 'Siap untuk diunggah ulang dengan aman.'}
                      </span>
                      <button
                        type="button"
                        onClick={handleResetUncertainReceipt}
                        disabled={isUploadingReceipt || isBackgroundUploadPending}
                        className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-colors ${
                          isUploadingReceipt || isBackgroundUploadPending
                            ? 'bg-amber-200 text-amber-500 cursor-not-allowed'
                            : 'bg-[#0A192F] text-white hover:bg-slate-800 cursor-pointer'
                        }`}
                      >
                        Atur Ulang & Pilih File
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
              </>
            )}
          </section>

          {/* Voucher Section (Hanya untuk member terautentikasi) */}
          {customerProfile && (
            <section className="bg-white rounded-2xl p-4 border border-[#E2E8F0] shadow-2xs space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Ticket className="w-4 h-4 text-[#0056D2]" />
                  <h3 className="font-bold text-sm text-[#0A192F]">Voucher Reward</h3>
                </div>
                {selectedVoucher ? (
                  <button
                    type="button"
                    onClick={() => setSelectedVoucher(null)}
                    className="text-xs font-semibold text-rose-600 hover:text-rose-700 cursor-pointer"
                  >
                    Hapus Voucher
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setIsVoucherModalOpen(true)}
                    className="text-xs font-bold text-[#0056D2] hover:underline flex items-center gap-1 cursor-pointer"
                  >
                    <span>Pilih Voucher</span>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {selectedVoucher ? (
                <div className="p-3 rounded-xl bg-blue-50/80 border border-blue-200 flex items-center justify-between">
                  <div className="space-y-0.5">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-[#0056D2]">{selectedVoucher.rewardName}</span>
                      <span className="text-[10px] font-mono bg-blue-100 text-[#0056D2] px-1.5 py-0.5 rounded font-semibold">
                        {selectedVoucher.voucherCode}
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-600">
                      Diskon: <strong className="text-emerald-600">-{formatRupiah(voucherDiscount)}</strong>
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedVoucher(null);
                      if (formError && (formError.toLowerCase().includes('voucher') || formError.toLowerCase().includes('digunakan'))) {
                        setFormError('');
                      }
                    }}
                    className="p-1.5 text-slate-400 hover:text-rose-600 rounded-lg cursor-pointer"
                    title="Hapus Voucher"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div
                  onClick={() => {
                    if (formError && (formError.toLowerCase().includes('voucher') || formError.toLowerCase().includes('digunakan'))) {
                      setFormError('');
                    }
                    setIsVoucherModalOpen(true);
                  }}
                  className="p-3 rounded-xl bg-slate-50 border border-dashed border-slate-300 flex items-center justify-between cursor-pointer hover:bg-slate-100/70 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Tag className="w-3.5 h-3.5 text-slate-400" />
                    <span className="text-xs text-slate-600">
                      {isLoadingVouchers
                        ? 'Memeriksa voucher...'
                        : availableVouchers.length > 0
                        ? `${availableVouchers.length} voucher aktif tersedia`
                        : 'Belum ada voucher aktif'}
                    </span>
                  </div>
                  <span className="text-xs font-bold text-[#0056D2]">
                    {availableVouchers.length > 0 ? 'Gunakan' : 'Lihat'}
                  </span>
                </div>
              )}
            </section>
          )}

          {/* Payment Details Breakdown (UX Pilot Screen 6) */}
          <section className="bg-white rounded-2xl p-4 border border-[#E2E8F0] shadow-2xs space-y-2.5">
            <h3 className="font-bold text-sm text-[#0A192F] mb-1">Rincian Pembayaran</h3>
            <div className="flex justify-between text-xs text-[#64748B]">
              <span>Subtotal Pesanan ({totalItems} item)</span>
              <span className="font-semibold text-[#0A192F]">{formatRupiah(subtotal)}</span>
            </div>
            {voucherDiscount > 0 && selectedVoucher && (
              <div className="flex justify-between text-xs text-emerald-600 font-medium bg-emerald-50/60 px-2 py-1.5 rounded-lg border border-emerald-100">
                <span className="flex items-center gap-1">
                  <Ticket className="w-3.5 h-3.5" />
                  <span>Voucher ({selectedVoucher.voucherCode})</span>
                </span>
                <span className="font-bold">-{formatRupiah(voucherDiscount)}</span>
              </div>
            )}
            {orderType === 'DELIVERY' && (
              <div className="flex justify-between text-xs text-[#0A192F] bg-blue-50/50 px-2 py-1.5 rounded-lg border border-blue-100">
                <span className="flex items-center gap-1.5 font-medium">
                  <Truck className="w-3.5 h-3.5 text-[#0056D2]" />
                  <span>Ongkos Kirim {deliveryDistance !== null ? `(${deliveryDistance.toFixed(1)} km)` : ''}</span>
                </span>
                <span className="font-bold text-[#0056D2]">
                  {deliveryFee > 0
                    ? formatRupiah(deliveryFee)
                    : isDistanceExceeded
                    ? 'Jarak > 10 km'
                    : 'Perlu GPS'}
                </span>
              </div>
            )}
            <div className="flex justify-between text-xs text-[#64748B]">
              <span>Biaya Layanan</span>
              <span className="font-semibold text-[#0A192F]">{formatRupiah(serviceFee)}</span>
            </div>
            <div className="border-t border-dashed border-[#E2E8F0] pt-2.5 mt-1 flex justify-between items-center">
              <span className="font-bold text-sm text-[#0A192F]">Total Pembayaran</span>
              <span className="font-extrabold text-lg text-[#0056D2]">{formatRupiah(grandTotal)}</span>
            </div>
          </section>
        </form>
      </main>

      {/* Modal Pemilihan Voucher */}
      {isVoucherModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-2xl max-h-[85vh] flex flex-col shadow-2xl overflow-hidden">
            <div className="p-4 border-b border-[#E2E8F0] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Ticket className="w-5 h-5 text-[#0056D2]" />
                <h3 className="font-bold text-base text-[#0A192F]">Pilih Voucher Reward</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsVoucherModalOpen(false)}
                className="p-1.5 text-slate-400 hover:text-slate-700 rounded-full hover:bg-slate-100 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto space-y-3 flex-1">
              {isLoadingVouchers ? (
                <div className="py-8 text-center text-xs text-slate-500">
                  Memuat daftar voucher Anda...
                </div>
              ) : (allRedemptions.length > 0 ? allRedemptions : availableVouchers).length === 0 ? (
                <div className="py-10 text-center space-y-2">
                  <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto text-slate-400">
                    <Ticket className="w-6 h-6" />
                  </div>
                  <h4 className="font-bold text-sm text-[#0A192F]">Belum Ada Voucher Aktif</h4>
                  <p className="text-xs text-[#64748B] max-w-xs mx-auto">
                    Tukarkan poin loyalitas Anda di menu profil untuk mendapatkan voucher potongan harga.
                  </p>
                </div>
              ) : (
                ((allRedemptions.length > 0 ? allRedemptions : availableVouchers)).map((v) => {
                  const isSelected = selectedVoucher?.id === v.id;
                  const isFreeItem = v.rewardType === 'FREE_ITEM';
                  const isActive = v.status === 'ACTIVE';

                  // Hitung estimasi diskon untuk voucher ini
                  let estDiscount = 0;
                  if (v.rewardType === 'DISCOUNT_NOMINAL') {
                    estDiscount = Math.min(Number(v.rewardValue || 0), subtotal);
                  } else if (v.rewardType === 'DISCOUNT_PERCENT') {
                    estDiscount = Math.min(Math.floor((subtotal * Number(v.rewardValue || 0)) / 100), subtotal);
                  }

                  return (
                    <div
                      key={v.id}
                      className={`p-3.5 rounded-2xl border transition-all ${
                        isFreeItem
                          ? 'bg-slate-50 border-slate-200 opacity-70'
                          : isSelected
                          ? 'bg-blue-50/70 border-[#0056D2] shadow-xs'
                          : 'bg-white border-[#E2E8F0] hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="space-y-1 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-bold text-sm text-[#0A192F]">{v.rewardName}</span>
                            <span className="text-[10px] font-mono bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded font-semibold">
                              {v.voucherCode}
                            </span>
                          </div>

                          <p className="text-xs text-[#64748B]">
                            {v.rewardType === 'DISCOUNT_NOMINAL' && `Potongan langsung Rp ${Number(v.rewardValue).toLocaleString('id-ID')}`}
                            {v.rewardType === 'DISCOUNT_PERCENT' && `Diskon ${v.rewardValue}%`}
                            {v.rewardType === 'FREE_ITEM' && 'Hadiah Item Gratis'}
                          </p>

                          {isFreeItem ? (
                            <p className="text-[11px] text-amber-700 bg-amber-50 px-2 py-1 rounded-md mt-1 font-medium">
                              Voucher Free Item belum didukung pada checkout online. Tunjukkan voucher ini ke kasir saat berkunjung.
                            </p>
                          ) : (
                            <p className="text-[11px] text-emerald-700 font-semibold">
                              Hemat: {formatRupiah(estDiscount)}
                            </p>
                          )}

                          {v.expiresAt && (
                            <p className="text-[10px] text-slate-400">
                              Berlaku hingga: {new Date(v.expiresAt).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </p>
                          )}
                        </div>

                        {!isFreeItem && isActive && (
                          <button
                            type="button"
                            onClick={() => {
                              if (isSelected) {
                                setSelectedVoucher(null);
                              } else {
                                setSelectedVoucher(v);
                                setIsVoucherModalOpen(false);
                              }
                            }}
                            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-colors cursor-pointer shrink-0 ${
                              isSelected
                                ? 'bg-rose-50 text-rose-600 hover:bg-rose-100 border border-rose-200'
                                : 'bg-[#0056D2] text-white hover:bg-blue-700'
                            }`}
                          >
                            {isSelected ? 'Batal' : 'Pakai'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="p-3 border-t border-[#E2E8F0] bg-slate-50 text-right">
              <button
                type="button"
                onClick={() => setIsVoucherModalOpen(false)}
                className="px-4 py-2 rounded-xl text-xs font-bold text-slate-700 hover:bg-slate-200 transition-colors cursor-pointer"
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fixed Bottom Action Bar (UX Pilot Screen 6) */}
      <div
        className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E2E8F0] p-4 shadow-[0_-8px_20px_rgba(10,25,47,0.06)] z-40"
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}
      >
        <div className="max-w-lg mx-auto">
          {formError && (
            <div className="mb-2 p-2.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-center gap-2 font-medium">
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
              <span className="flex-1">{formError}</span>
            </div>
          )}
          <div className="flex items-center justify-between mb-3 px-1">
            <div>
              <span className="text-[10px] font-semibold text-[#64748B] uppercase tracking-wider block">
                Total to Pay
              </span>
              <span className="font-extrabold text-xl text-[#0A192F]">{formatRupiah(grandTotal)}</span>
            </div>
            <div className="text-right">
              <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" /> Verified Order
              </span>
            </div>
          </div>
           <button
            type="button"
            onClick={handleSubmit}
            disabled={isPlaceOrderDisabled}
            id="submit-order-btn"
            className={`w-full font-bold py-3.5 rounded-xl shadow-md transition-all flex items-center justify-center gap-2 text-sm ${
              isPlaceOrderDisabled
                ? 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none'
                : paymentMethod === 'DANA'
                ? 'bg-[#118EEA] hover:bg-sky-600 text-white cursor-pointer active:scale-[0.99]'
                : 'bg-[#0056D2] hover:bg-blue-700 text-white cursor-pointer active:scale-[0.99]'
            }`}
          >
            {!isOutletOpen ? (
              <>
                <Lock className="w-4 h-4" />
                <span>Outlet Closed</span>
              </>
            ) : isSubmitting || isInitiatingDana ? (
              <span>{paymentMethod === 'DANA' ? 'Menyiapkan Pembayaran DANA...' : 'Processing Order...'}</span>
            ) : paymentMethod === 'DANA' ? (
              <>
                <ExternalLink className="w-4 h-4" />
                <span>Bayar dengan DANA</span>
              </>
            ) : (
              <>
                <Lock className="w-4 h-4 text-blue-200" />
                <span>Pay with QRIS</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};




